import type {
  Hint,
  MarketplaceTool,
  PluginContext,
  PluginDataScope,
  PluginSurface,
  ToolView,
} from "@scoach/types";
import { Bolt } from "@scoach/ui/icons";
import { useEffect, useMemo, useState } from "react";

import { DashHeader } from "../dashboard/components/DashHeader.tsx";
import { PluginHost } from "../plugins/PluginHost.tsx";
import { pluginsApi } from "../plugins/api.ts";
import { SuggestPluginModal } from "./SuggestPluginModal.tsx";
import { TokensTab } from "./TokensTab.tsx";

type DevTab = "docs" | "playground" | "mine" | "tokens";

function mockContext(scope: PluginDataScope, surface: PluginSurface): PluginContext {
  const ctx: PluginContext = {
    surface,
    dataScope: scope,
    meeting: {
      id: "demo-meeting",
      company: "Acme Corp",
      meetingType: "sales",
      role: "Customer Engineer",
    },
    hints: [
      {
        id: "h1",
        title: "Lead with the migration path",
        summary: "They're comparing to their current stack.",
        confidence: 0.9,
      } as unknown as Hint,
    ],
    followups: [
      "What does your current architecture look like?",
      "Who owns the migration decision?",
    ],
  };
  if (scope === "full") {
    ctx.transcript = [
      {
        id: "t1",
        t: "00:03",
        speaker: "client",
        name: "Client",
        lang: "en",
        text: "How does this compare to what we run today?",
        isFinal: true,
      },
      {
        id: "t2",
        t: "00:08",
        speaker: "rep",
        name: "You",
        lang: "en",
        text: "Great question — let me walk you through it.",
        isFinal: true,
      },
    ];
    ctx.summary = null;
  }
  return ctx;
}

export function DevelopersScreen() {
  const [tab, setTab] = useState<DevTab>("docs");
  const [suggestOpen, setSuggestOpen] = useState(false);

  return (
    <div className="dash">
      <DashHeader />
      <div className="clients-body">
        <div className="clients-header" style={{ flexWrap: "wrap", gap: 12 }}>
          <h2 className="clients-title">
            <Bolt size={20} /> Developer hub
          </h2>
          <div style={{ flex: 1 }} />
          <button type="button" className="pill-btn primary" onClick={() => setSuggestOpen(true)}>
            Submit a plugin
          </button>
        </div>

        <div className="seg seg-tabs" style={{ marginBottom: 16 }}>
          {(
            [
              ["docs", "Docs"],
              ["playground", "Playground"],
              ["mine", "My plugins"],
              ["tokens", "Tokens & API"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={tab === k ? "on" : ""}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "docs" && <DocsTab />}
        {tab === "playground" && <PlaygroundTab onSuggest={() => setSuggestOpen(true)} />}
        {tab === "mine" && <MyPluginsTab />}
        {tab === "tokens" && <TokensTab />}
      </div>

      <SuggestPluginModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onSubmitted={() => {}}
      />
    </div>
  );
}

function DocsTab() {
  return (
    <div style={{ maxWidth: 820, fontSize: 14, lineHeight: 1.6, color: "var(--text-2)" }}>
      <h3>Build a Sally plugin</h3>
      <p>
        A plugin is a web page you host. Sally embeds it in a <strong>sandboxed iframe</strong> in
        the Plugins tab on the live and/or summary screen, and talks to it over{" "}
        <code>postMessage</code>. Only admin-approved plugin origins can load (CSP allowlist).
      </p>
      <h4>1. Receive context</h4>
      <p>
        On load, Sally sends an <code>init</code>, then <code>update</code>s as the meeting changes:
      </p>
      <pre style={preStyle}>{`window.addEventListener("message", (e) => {
  const m = e.data;
  if (m?.type === "sally:init")   { start(m.nonce, m.capabilities, m.context); }
  if (m?.type === "sally:update") { render(m.context); }
});`}</pre>
      <p>
        <code>context</code> ={" "}
        <code>{`{ surface, dataScope, meeting:{id,company,meetingType,role}, hints[], followups[] }`}</code>
        . If your approved data scope is <strong>full</strong>, it also includes{" "}
        <code>transcript[]</code> and (on the summary) <code>summary</code>.
      </p>
      <h4>2. Request actions</h4>
      <p>
        Send back one of your approved actions (echo the <code>nonce</code> from init):
      </p>
      <pre style={preStyle}>{`parent.postMessage({
  type: "sally:action", nonce,
  action: "add-note",           // add-note | add-hint | open-url | set-tag
  payload: { text: "Follow up on pricing" }
}, "*");`}</pre>
      <h4>3. Submit</h4>
      <p>
        Use the <strong>Playground</strong> to test against mock data, then{" "}
        <strong>Submit a plugin</strong>. It runs an automated security review; if issues are found
        they appear under <strong>My plugins</strong>
        for you to fix and resubmit. Once it passes, an admin approves it and sets the final data
        scope, actions, and surfaces.
      </p>
      <h3 style={{ marginTop: 28 }}>Personal access tokens &amp; the API</h3>
      <p>
        Create a token in the <strong>Tokens &amp; API</strong> tab. A token authenticates an
        external tool (or the Sally MCP server) to <strong>your own data only</strong> — your
        meetings, tasks, summaries and transcripts. It can never read anyone else's data, and you
        can revoke it anytime. The raw token (<code>sal_…</code>) is shown once at creation.
      </p>
      <p>
        Call the API with your token in the <code>X-Sally-Token</code> header. Base URL is this
        Sally origin. All endpoints are scoped to the token's owner.
      </p>
      <pre style={preStyle}>{`# List your meetings
curl -H "X-Sally-Token: sal_xxx" \\
  https://<sally-host>/public/meetings

# Get a meeting summary / transcript / your tasks
curl -H "X-Sally-Token: sal_xxx" https://<sally-host>/public/meetings/<id>
curl -H "X-Sally-Token: sal_xxx" https://<sally-host>/public/meetings/<id>/transcript
curl -H "X-Sally-Token: sal_xxx" https://<sally-host>/public/tasks

# Add a note
curl -X POST -H "X-Sally-Token: sal_xxx" -H "Content-Type: application/json" \\
  -d '{"text":"Follow up on pricing"}' https://<sally-host>/public/meetings/<id>/notes

# Create a new task on a meeting
curl -X POST -H "X-Sally-Token: sal_xxx" -H "Content-Type: application/json" \\
  -d '{"what":"Send proposal","who":"me","due":"2026-09-01"}' \\
  https://<sally-host>/public/meetings/<id>/tasks

# Update a task (owner / text / due date / done) — taskId is <meetingId>::<itemId>
curl -X PATCH -H "X-Sally-Token: sal_xxx" -H "Content-Type: application/json" \\
  -d '{"due":"2026-09-15","done":true}' https://<sally-host>/public/tasks/<meetingId>::<itemId>

# Mark a task done (shorthand)
curl -X POST -H "X-Sally-Token: sal_xxx" https://<sally-host>/public/tasks/<meetingId>::<itemId>/complete`}</pre>
      <p style={{ fontSize: 13, color: "var(--text-3)" }}>
        Endpoints: <code>GET /public/me</code>, <code>/public/meetings</code>,{" "}
        <code>/public/meetings/:id</code> (summary — includes the client email draft),{" "}
        <code>/public/meetings/:id/transcript</code>, <code>/public/tasks</code>;{" "}
        <code>POST /public/meetings/:id/notes</code>, <code>POST /public/meetings/:id/tasks</code>{" "}
        (create), <code>PATCH /public/tasks/:id</code> (update who/what/due/done),{" "}
        <code>POST /public/tasks/:id/complete</code>. Rate limit: 120 requests/min per token.
      </p>

      <McpDocs />

      <p style={{ fontSize: 13, color: "var(--text-3)", marginTop: 24 }}>
        Security: a token only ever accesses its owner's data; revoke it anytime in Tokens &amp;
        API. Don't commit tokens to source control.
      </p>
    </div>
  );
}

const preStyle: React.CSSProperties = {
  background: "var(--surface-2, #f6f8fc)",
  border: "1px solid var(--border-soft)",
  borderRadius: 8,
  padding: 12,
  fontSize: 12.5,
  overflowX: "auto",
};

const thtd: React.CSSProperties = {
  border: "1px solid var(--border-soft)",
  padding: "8px 10px",
  textAlign: "left",
  verticalAlign: "top",
  fontSize: 12.5,
};

/** Comprehensive, self-contained Sally MCP documentation shown in the Docs tab. */
function McpDocs() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://<sally-host>";
  const config = `{
  "mcpServers": {
    "sally": {
      "command": "node",
      "args": ["/absolute/path/to/sally-mcp.mjs"],
      "env": {
        "SALLY_BASE_URL": "${origin}",
        "SALLY_TOKEN": "sal_YOUR_TOKEN_HERE"
      }
    }
  }
}`;

  const CAPS: Array<[string, string, string, string]> = [
    [
      "Meetings",
      "id, title, client, type (technical/sales/review/upsell/other), status (draft/live/ended/summarized), date",
      "Read",
      "list_meetings",
    ],
    [
      "Transcripts",
      "Timestamped verbatim lines: t, text, speaker (rep/client), name, lang, isFinal",
      "Read",
      "get_transcript",
    ],
    [
      "Summary",
      "Executive overview, health (warm/cold), score & confidence, key topics, requirements & blockers",
      "Read",
      "get_summary",
    ],
    [
      "Client email draft",
      "subject, greeting, body[], signoff, tone — a ready-to-send follow-up (inside the summary)",
      "Read",
      "get_summary",
    ],
    ["References", "Curated docs/architecture links: title, href, source", "Read", "get_summary"],
    [
      "Tasks / action items",
      "what, who, due (ISO), done — query, create, update, complete",
      "Read + Write",
      "list_tasks · add_task · update_task · complete_task",
    ],
    ["Notes", "Private per-meeting annotations (text)", "Write", "add_note"],
  ];

  const TOOLS: Array<[string, string, string]> = [
    ["list_meetings", "— (none)", "Your meetings (id, title, client, type, status, date)."],
    ["get_summary", "meetingId", "Overview, health/score, the client email draft, references."],
    ["get_transcript", "meetingId", "Line-by-line dialogue (timestamp, speaker, text, lang)."],
    ["list_tasks", "status? (open|done)", "Your action items across meetings."],
    ["add_task", "meetingId, what, who?, due?", "Create a new action item on a meeting."],
    [
      "update_task",
      "taskId, who?, what?, due?, done?",
      "Update an action item (taskId = <meetingId>::<itemId>).",
    ],
    ["complete_task", "taskId, done? (default true)", "Mark a task done / reopen it."],
    ["add_note", "meetingId, text", "Append a private note to a meeting."],
  ];

  return (
    <>
      <h3 style={{ marginTop: 28 }}>Connect your tools — the Sally MCP server</h3>
      <p>
        The Sally MCP server connects AI assistants (Claude Code, Cursor, Antigravity, Windsurf,
        Spark, …) directly to <strong>your own</strong> Sally meetings, transcripts, summaries and
        tasks. It's a single <strong>zero-dependency</strong> file (no <code>npm install</code>, no
        registry) that speaks the MCP stdio protocol and calls Sally's owner-scoped{" "}
        <code>/public/*</code> API.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0 4px" }}>
        <a className="pill-btn primary" href={`${origin}/sally-mcp.mjs`} download="sally-mcp.mjs">
          Download sally-mcp.mjs
        </a>
        <a className="pill-btn" href={`${origin}/sally-mcp-docs.md`} download="SALLY_MCP.md">
          Download full guide (.md)
        </a>
      </div>

      <h4>How it's secured (two layers)</h4>
      <ol style={{ paddingLeft: 18 }}>
        <li>
          <strong>Network — Google IAP.</strong> Sally sits behind IAP (Google-managed OAuth
          client). The server automatically mints and refreshes a short-lived service-account
          self-signed JWT (<code>aud = {"<base-url>/*"}</code>, via the IAM <code>signJwt</code>{" "}
          API) using your own <code>gcloud</code> identity — no manual token, no hourly expiry.
        </li>
        <li>
          <strong>Data — your access token.</strong> Every request also carries{" "}
          <code>X-Sally-Token: sal_…</code>. The backend scopes all data strictly to the token's
          owner. An agent can never reach anyone else's meetings, tasks or transcripts.
        </li>
      </ol>

      <h4>Prerequisites</h4>
      <ul style={{ paddingLeft: 18 }}>
        <li>Node.js 18+.</li>
        <li>
          Signed in with the gcloud CLI: <code>gcloud auth login</code> (once). No key files, no
          manual IAP token.
        </li>
        <li>
          A personal access token from <strong>Tokens &amp; API</strong> (starts with{" "}
          <code>sal_</code>).
        </li>
      </ul>

      <h4>Setup</h4>
      <p>
        Download <code>sally-mcp.mjs</code> (button above) to a stable path, then add this to your
        MCP client config — point <code>args</code> at the file and paste your token. Restart the
        client.
      </p>
      <pre style={preStyle}>{config}</pre>
      <p style={{ fontSize: 13, color: "var(--text-3)" }}>
        Works the same in <strong>Claude Code / Claude Desktop</strong>, <strong>Cursor</strong> (
        <code>.cursor/mcp.json</code>) and <strong>Antigravity</strong> (
        <code>~/.gemini/config/mcp_config.json</code> — there, set <code>command</code> to the
        bundled node, e.g. <code>~/.gemini/antigravity/bin/node</code>). Advanced IAP overrides:{" "}
        <code>SALLY_IAP_SA</code>, <code>SALLY_IAP_TOKEN_CMD</code>, <code>SALLY_IAP_TOKEN</code>.
      </p>

      <h4>What you can access</h4>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", margin: "6px 0" }}>
          <thead>
            <tr style={{ background: "var(--surface-2, #f6f8fc)" }}>
              <th style={{ ...thtd, fontWeight: 700 }}>Category</th>
              <th style={{ ...thtd, fontWeight: 700 }}>Fields</th>
              <th style={{ ...thtd, fontWeight: 700 }}>Access</th>
              <th style={{ ...thtd, fontWeight: 700 }}>Tool(s)</th>
            </tr>
          </thead>
          <tbody>
            {CAPS.map(([cat, fields, access, tools]) => (
              <tr key={cat}>
                <td style={{ ...thtd, fontWeight: 600, whiteSpace: "nowrap" }}>{cat}</td>
                <td style={thtd}>{fields}</td>
                <td style={{ ...thtd, whiteSpace: "nowrap" }}>{access}</td>
                <td style={thtd}>
                  <code>{tools}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4>Tool reference</h4>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", margin: "6px 0" }}>
          <thead>
            <tr style={{ background: "var(--surface-2, #f6f8fc)" }}>
              <th style={{ ...thtd, fontWeight: 700 }}>Tool</th>
              <th style={{ ...thtd, fontWeight: 700 }}>Parameters</th>
              <th style={{ ...thtd, fontWeight: 700 }}>Returns / effect</th>
            </tr>
          </thead>
          <tbody>
            {TOOLS.map(([name, params, desc]) => (
              <tr key={name}>
                <td style={{ ...thtd, whiteSpace: "nowrap" }}>
                  <code>{name}</code>
                </td>
                <td style={{ ...thtd, whiteSpace: "nowrap" }}>
                  <code>{params}</code>
                </td>
                <td style={thtd}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4>Example — get a meeting summary</h4>
      <pre style={preStyle}>{`get_summary { "meetingId": "fd5bdd47-…-200c7da4c445" }
→ {
  "internal": { "health": "warm", "score": 85,
    "overview": { "summary": "Explored cloud & GenAI architecture…" } },
  "client": { "subject": "Thank you for connecting | Next Steps",
    "greeting": "Hi team,", "body": ["Thanks for the time today…"],
    "signoff": "Warm regards" },
  "references": [{ "title": "Vertex AI Overview",
    "href": "https://cloud.google.com/vertex-ai", "source": "cloud.google.com" }]
}`}</pre>

      <h4>Troubleshooting</h4>
      <ul style={{ paddingLeft: 18 }}>
        <li>
          <strong>401 “Missing / invalid X-Sally-Token”</strong> — set (or re-create) your{" "}
          <code>SALLY_TOKEN</code> in the config's <code>env</code>. This means IAP passed and the
          app rejected the Sally token.
        </li>
        <li>
          <strong>“IAP rejected the request” / signJwt denied</strong> — run{" "}
          <code>gcloud auth login</code> with your authorized Google account. A plain{" "}
          <code>gcloud auth print-identity-token</code> won't work (wrong audience); the server uses
          a self-signed SA JWT instead.
        </li>
        <li>
          <strong>“Unknown tool …”</strong> — restart / refresh MCP servers in your client so it
          re-reads the tool list.
        </li>
      </ul>
    </>
  );
}

function PlaygroundTab({ onSuggest }: { onSuggest: () => void }) {
  const [url, setUrl] = useState("");
  const [loaded, setLoaded] = useState("");
  const [scope, setScope] = useState<PluginDataScope>("metadata");
  const [surface, setSurface] = useState<PluginSurface>("live");
  const [log, setLog] = useState<string[]>([]);

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      const m = e.data;
      if (m?.type === "sally:action") {
        setLog((l) =>
          [`◀ action ${m.action} ${JSON.stringify(m.payload ?? {})}`, ...l].slice(0, 50),
        );
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // A pretend "plugin" ToolView so PluginHost can render an arbitrary URL with all actions.
  const plugin = useMemo(
    () =>
      ({
        id: "playground",
        name: "Playground",
        embedUrl: loaded,
        actions: ["add-note", "add-hint", "open-url", "set-tag"],
        dataScope: scope,
      }) as unknown as ToolView,
    [loaded, scope],
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <input
            className="task-edit-input"
            style={{ flex: 1, minWidth: 220 }}
            placeholder="https://your-plugin.example.com/widget"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <select
            className="task-edit-input"
            value={scope}
            onChange={(e) => setScope(e.target.value as PluginDataScope)}
          >
            <option value="metadata">metadata</option>
            <option value="full">full</option>
          </select>
          <select
            className="task-edit-input"
            value={surface}
            onChange={(e) => setSurface(e.target.value as PluginSurface)}
          >
            <option value="live">live</option>
            <option value="summary">summary</option>
          </select>
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => {
              setLoaded(url.trim());
              setLog([]);
            }}
            disabled={!url.trim()}
          >
            Load
          </button>
          <button type="button" className="pill-btn" onClick={onSuggest}>
            Submit this plugin
          </button>
        </div>
        <div
          style={{
            height: 460,
            border: "1px solid var(--border-soft)",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {loaded ? (
            <PluginHost plugin={plugin} context={mockContext(scope, surface)} height={458} />
          ) : (
            <div
              className="tasks-empty"
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Enter a plugin URL and press Load to test it against mock meeting data.
            </div>
          )}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Message inspector</div>
        <div
          style={{
            border: "1px solid var(--border-soft)",
            borderRadius: 8,
            padding: 8,
            height: 460,
            overflowY: "auto",
            fontSize: 12,
            fontFamily: "var(--font-mono, monospace)",
          }}
        >
          {log.length === 0 ? (
            <span style={{ color: "var(--text-3)" }}>
              Actions your plugin sends will appear here.
            </span>
          ) : (
            log.map((l, i) => <div key={i}>{l}</div>)
          )}
        </div>
      </div>
    </div>
  );
}

function stateLabel(s?: string): { text: string; color: string } {
  switch (s) {
    case "approved":
      return { text: "Approved", color: "#137333" };
    case "awaiting-admin":
      return { text: "Awaiting admin", color: "#1967d2" };
    case "security-review":
      return { text: "In security review", color: "#b06000" };
    case "changes-requested":
      return { text: "Changes requested", color: "#d93025" };
    case "rejected":
      return { text: "Rejected", color: "#d93025" };
    default:
      return { text: s ?? "Submitted", color: "#5f6368" };
  }
}

function MyPluginsTab() {
  const [plugins, setPlugins] = useState<MarketplaceTool[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function refresh() {
    pluginsApi
      .mine()
      .then((r) => setPlugins(r.plugins))
      .catch(() => setPlugins([]));
  }
  useEffect(refresh, []);

  async function resubmit(id: string) {
    setBusyId(id);
    try {
      await pluginsApi.resubmit(id);
      refresh();
    } finally {
      setBusyId(null);
    }
  }

  async function publish(id: string) {
    setBusyId(id);
    try {
      await pluginsApi.publish(id);
      refresh();
    } finally {
      setBusyId(null);
    }
  }

  if (plugins === null) return <div className="tasks-empty">Loading…</div>;
  if (plugins.length === 0)
    return <div className="tasks-empty">You haven't submitted any plugins yet.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 820 }}>
      {plugins.map((p) => {
        const s = stateLabel(p.reviewState);
        return (
          <div
            key={p.id}
            style={{ border: "1px solid var(--border-soft)", borderRadius: 12, padding: 16 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{p.name}</strong>
              <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>
                {p.visibility === "private" ? "Private — only you" : s.text}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-3)", margin: "2px 0 8px" }}>
              {p.embedUrl}
            </div>
            {p.securityReport && p.securityReport.findings.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Security findings
                </div>
                {p.securityReport.findings.map((f, i) => (
                  <div key={i} style={{ fontSize: 12.5, marginBottom: 4 }}>
                    <span
                      style={{
                        fontWeight: 700,
                        color:
                          f.severity === "high"
                            ? "#d93025"
                            : f.severity === "medium"
                              ? "#b06000"
                              : "#5f6368",
                      }}
                    >
                      [{f.severity}]
                    </span>{" "}
                    <strong>{f.title}</strong> — {f.detail}
                  </div>
                ))}
              </div>
            )}
            {(p.reviewState === "changes-requested" || p.reviewState === "security-review") && (
              <button
                type="button"
                className="pill-btn"
                onClick={() => void resubmit(p.id)}
                disabled={busyId === p.id}
              >
                {busyId === p.id ? "Re-scanning…" : "Resubmit (re-run scan)"}
              </button>
            )}
            {p.visibility === "private" && (
              <button
                type="button"
                className="pill-btn primary"
                onClick={() => void publish(p.id)}
                disabled={busyId === p.id}
                title="Send to security review + admin approval so everyone can use it"
              >
                {busyId === p.id ? "Publishing…" : "Publish to everyone"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
